// frontend/src/services/productService.ts
import { api } from './api';

/**
 * Interfaces para Productos
 */
export interface Product {
  id_producto: number;
  cod_producto: string | null;
  color: string | null;
  marca: string | null;
  precio: number | null;
  costo?: number | null;
  talla: string | null;
  created_at: Date;
  stock: number | null;
  activo: boolean | null;
  pre_order: boolean | null;
  codigo_proveedor: string | null;
  qr_code_value?: string | null;
  in_transit?: boolean | null;
  saleFolio?: string | null;
  invoice_id?: string | null;
  inventoryType?: 'maestro' | 'novias';
  received_at?: string | Date | null;
  rebajado?: boolean | null;
  estado_entrega?: string | null;
  fecha_entrega?: Date | string | null;
  isTemp?: boolean;
  sellerRole?: string | null;
  preOrderDetails?: {
    sellerName: string;
    customerName: string;
    date: string | Date;
    total: number;
    folio: string;
    paymentMethod?: string;
    status?: string;
  } | null;
}

export interface ProductFilters {
  page?: number;
  limit?: number;
  search?: string;
  color?: string;
  talla?: string;
  marca?: string;
  type?: 'maestro' | 'novias' | 'all';
  pending?: boolean;
  preOrder?: boolean;
  outOfStock?: boolean;
  inactive?: boolean;
  exactMatch?: boolean;
  invoiceId?: string;
  recent?: boolean;
  showDelivered?: boolean;
  sortBy?: string;
  order?: 'asc' | 'desc';
  _t?: number; // cache-buster
}

export interface ProductsResponse {
  success: boolean;
  data: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface FilterOptions {
  colores: string[];
  tallas: string[];
  marcas: string[];
  codes: string[];
  brandColors: { brand: string; color: string }[];
  invoices: string[];

  // New separated structures
  maestro?: {
    colores: string[];
    tallas: string[];
    marcas: string[];
    codes: string[];
    brandColors: { brand: string; color: string }[];
    brandSizes: { brand: string; size: string }[];
    codeBrands: { code: string; brand: string }[];
  };
  novias?: {
    colores: string[];
    tallas: string[];
    marcas: string[];
    codes: string[];
    brandColors: { brand: string; color: string }[];
    brandSizes: { brand: string; size: string }[];
    codeBrands: { code: string; brand: string }[];
  };
}

export interface CreateProductData {
  cod_producto: string;
  color?: string;
  marca?: string;
  precio?: number;
  costo?: number;
  talla?: string;
  stock?: number;
  activo?: boolean;
  pre_order?: boolean;
  codigo_proveedor?: string;
  qr_code_value?: string;
  generate_qr?: boolean;
  type?: 'maestro' | 'novias';
}

export interface UpdateStockData {
  stock: number;
  operation: 'add' | 'subtract' | 'set';
}

/**
 * Servicio de Productos
 */
class ProductService {
  /**
   * Obtener todos los productos con filtros
   */
  async getProducts(filters: ProductFilters = {}): Promise<ProductsResponse> {
    const response = await api.get('/products', { params: filters });
    return response.data;
  }

  /**
   * Obtener un producto por ID
   */
  async getProductById(id: number): Promise<Product> {
    const response = await api.get(`/products/${id}`);
    return response.data.data;
  }

  /**
   * Crear un nuevo producto
   */
  async createProduct(data: CreateProductData): Promise<Product> {
    const response = await api.post('/products', data);
    return response.data.data;
  }

  /**
   * Actualizar un producto
   */
  async updateProduct(id: number, data: Partial<CreateProductData>): Promise<any> {
    const response = await api.put(`/products/${id}`, data);
    // If backend returns inventoryMatch or swapped, return full response for handling
    if (response.data.inventoryMatch || response.data.swapped) {
      return response.data;
    }
    return response.data.data;
  }

  /**
   * Eliminar un producto (soft/hard delete automático)
   */
  async deleteProduct(id: number, type?: string): Promise<any> {
    const url = type ? `/products/${id}?type=${type}` : `/products/${id}`;
    const response = await api.delete(url);
    return response.data;
  }

  /**
   * Verificar dependencias antes de eliminar un producto
   */
  async checkProductDependencies(id: number, type?: string): Promise<any> {
    const url = type ? `/products/${id}/delete-dependencies?type=${type}` : `/products/${id}/delete-dependencies`;
    const response = await api.get(url);
    return response.data;
  }

  /**
   * Activar un producto desactivado
   */
  async activateProduct(id: number): Promise<Product> {
    const response = await api.patch(`/products/${id}/activate`);
    return response.data.data;
  }

  /**
   * Obtener opciones de filtros
   */
  async getFilterOptions(): Promise<FilterOptions> {
    const response = await api.get('/products/filters/options');
    return response.data.data;
  }

  /**
   * Actualizar stock de un producto
   */
  async updateStock(id: number, data: UpdateStockData): Promise<Product> {
    const response = await api.patch(`/products/${id}/stock`, data);
    return response.data.data;
  }

  /**
   * Procesar Invoice Masivo
   */
  async processInvoice(data: any): Promise<any> {
    const response = await api.post('/products/invoice', data);
    return response.data;
  }

  /**
   * Buscar coincidencias flexibles de Pre-orders para un array de productos de invoice
   */
  async getPreOrderMatches(products: any[]): Promise<any> {
    const response = await api.post('/products/preorder-matches', { products });
    return response.data;
  }

  /**
   * Validar posibles cruces de inventario (Maestro vs Novias) previos a guardar Invoice
   */
  async checkInventoryWarnings(products: any[]): Promise<any> {
    const response = await api.post('/products/inventory-warnings', { products });
    return response.data;
  }

  /**
   * Activar productos de un invoice
   */
  async activateInvoice(invoiceId: string): Promise<any> {
    const response = await api.post('/products/activate-invoice', { invoiceId });
    return response.data;
  }

  /**
   * Obtener lista de invoices con soporte para búsqueda y filtros
   */
  async getInvoices(params: { search?: string; status?: string } = {}): Promise<any> {
    const response = await api.get('/products/invoices', { params });
    return response.data;
  }

  /**
   * Revisar estado de Pre-orders huérfanos vs items del Invoice
   */
  async checkInvoicePreOrders(invoiceId: string): Promise<any> {
    const response = await api.get(`/products/invoices/${invoiceId}/check-preorders`);
    return response.data;
  }

  /**
   * Ejecutar corrección automática de Pre-orders para un Invoice
   */
  async correctInvoicePreOrders(invoiceId: string): Promise<any> {
    const response = await api.post(`/products/invoices/${invoiceId}/correct-preorders`);
    return response.data;
  }



  /**
   * Obtener tallas y colores disponibles para un modelo (variants)
   */
  async getVariants(code: string, type?: 'maestro' | 'novias'): Promise<{ sizes: string[], colors: string[] }> {
    try {
      const response = await this.getProducts({
        search: code,
        exactMatch: true,
        type: type || 'all',
        limit: 1000
      });
      if (response.success && response.data) {
        const exactData = response.data.filter(p => p.cod_producto && p.cod_producto.toUpperCase() === code.toUpperCase());
        const sizes = Array.from(new Set(exactData.map(p => p.talla).filter(Boolean))) as string[];
        const colors = Array.from(new Set(exactData.map(p => p.color).filter(Boolean))) as string[];
        return { sizes, colors };
      }
    } catch (e) {
      console.error('Error fetching variants', e);
    }
    return { sizes: [], colors: [] };
  }

  /**
   * Mover producto entre inventarios
   */
  async switchInventoryType(id: number, targetType: 'maestro' | 'novias'): Promise<any> {
    const response = await api.put(`/products/${id}/switch-type`, { targetType });
    return response.data;
  }
  /**
   * Corregir detalles de un Pre-order
   */
  async correctPreOrder(id: number, data: any): Promise<any> {
    const response = await api.put(`/products/${id}/correct-preorder`, data);
    return response.data;
  }

  /**
   * Marcar Pre-order como entregado
   */
  async deliverPreOrder(id: number, type: 'maestro' | 'novias'): Promise<any> {
    const response = await api.post(`/products/${id}/deliver`, { type });
    return response.data;
  }
  /**
   * Deshacer la entrega de un producto
   */
  async undoDeliverPreOrder(id: number, type: 'maestro' | 'novias'): Promise<any> {
    const response = await api.post(`/products/${id}/undo-delivery`, { type });
    return response.data;
  }

  /**
   * Verificar dependencias antes de eliminar un invoice completo
   */
  async checkInvoiceDependencies(invoiceId: string): Promise<any> {
    const response = await api.get(`/products/invoices/${invoiceId}/delete-dependencies`);
    return response.data;
  }

  /**
   * Eliminar un invoice completo
   */
  async deleteInvoice(invoiceId: string): Promise<any> {
    const response = await api.delete(`/products/invoices/${invoiceId}`);
    return response.data;
  }
}

export const productService = new ProductService();
export default productService;
